/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/manager/ShiftSwapReviewPage.tsx
 * PURPOSE: Màn "Đổi ca" riêng cho Quản Lý — Duyệt/Từ chối request đổi ca đã
 *          được NV target đồng ý. Tách khỏi ManagerApprovalQueue (nghỉ phép)
 *          theo yêu cầu 2026-08-30: "tạo riêng một màn hình... để có thể dễ
 *          cho việc làm dấu hiệu cảnh báo".
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-08-30
 * COMPLIANCE: SDLC 6.5.0 + Rule 3: Zero-Mock Tolerance
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Vietnamese F&B Workforce Management — Đổi ca (Variant B, DD-0106)
 * - Impact: Revenue Critical — duyệt sai/chậm gây thiếu người trên ca
 * - Stakeholders: Branch Managers
 *
 * @technical-architecture
 * - Layer: Presentation Layer (Page Component)
 * - Pattern: TanStack Query (usePendingSwapReviews, useApproveSwapRequest)
 * - Dependencies: managerApi (schedule/swap/*)
 *
 * @cross-references
 * - Decision: docs/02-design/18-adrs/06-decisions/DD-0106-shift-swap-variant-b-interim-canonical.md
 * - Backend: backend/app/api/v2/endpoints/schedule.py (swap/pending-manager-review, swap/approve)
 * - Hooks: frontend/src/hooks/useManagerSchedule.ts
 * - Route: frontend/src/App.tsx (/manager/shift-swaps)
 * - Style: tái dùng frontend/src/pages/manager/ManagerApprovalQueue.module.css
 * ===============================================================================
 */

import React, { useState } from "react";
import { usePendingSwapReviews, useApproveSwapRequest } from "../../hooks/useManagerSchedule";
import { extractApiError } from "./schedule/scheduleShared";
import type { ShiftSwapListItem } from "../../services/api/managerApi";
import styles from "./ManagerApprovalQueue.module.css";

const SwapIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={size} height={size} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
  </svg>
);

const WarningIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={size} height={size} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
  </svg>
);

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("vi-VN", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

const ShiftSwapReviewPage: React.FC = () => {
  const { data, isLoading, error, refetch } = usePendingSwapReviews();
  const approveMutation = useApproveSwapRequest();
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [toast, setToast] = useState<string | null>(null);

  const items: ShiftSwapListItem[] = data ?? [];

  const handleApprove = (item: ShiftSwapListItem) => {
    approveMutation.mutate(
      { swap_request_id: item.schedule_id, approved: true },
      {
        onSuccess: () => setToast(`Đã duyệt — ca chuyển sang ${item.target_name}.`),
        onError: (err) => setToast(extractApiError(err).message),
      },
    );
  };

  const startReject = (item: ShiftSwapListItem) => {
    setRejectingId(item.schedule_id);
    setRejectReason("");
  };

  const confirmReject = (item: ShiftSwapListItem) => {
    if (!rejectReason.trim()) return;
    approveMutation.mutate(
      { swap_request_id: item.schedule_id, approved: false, manager_notes: rejectReason.trim() },
      {
        onSuccess: () => {
          setRejectingId(null);
          setRejectReason("");
          setToast(`Đã từ chối — ${item.requester_name} có thể tìm người khác.`);
        },
        onError: (err) => setToast(extractApiError(err).message),
      },
    );
  };

  return (
    <div style={{ padding: "var(--nqh-space-4)" }}>
      <div className={styles.pageHead}>
        <h1 className={styles.pageHeadTitle}>Đổi ca</h1>
        <span className={styles.pageHeadCount}>
          {items.length} yêu cầu chờ duyệt (đã được nhân viên nhận đồng ý)
        </span>
      </div>

      {toast && (
        <div className={`${styles.banner} ${styles.bannerSuccess}`} role="status">
          {toast}
          <button onClick={() => setToast(null)} style={{ marginLeft: "auto" }}>✕</button>
        </div>
      )}

      {isLoading && <div className={styles.loadingState}>Đang tải…</div>}

      {error && (
        <div className={styles.errorState}>
          Lỗi tải danh sách. <button onClick={() => refetch()}>Thử lại</button>
        </div>
      )}

      {!isLoading && !error && items.length === 0 && (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>✅</div>
          <div className={styles.emptyTitle}>Không có yêu cầu đổi ca nào chờ duyệt</div>
          <div className={styles.emptySubtitle}>
            Request mới xuất hiện ở đây sau khi nhân viên được xin đổi ca đã đồng ý.
          </div>
        </div>
      )}

      <div className={styles.listWrap}>
        {items.map((item) => (
          <div
            key={item.schedule_id}
            className={styles.row}
            style={{
              // BUG THẬT đã sửa (2026-08-31, user báo "lỗi font chữ" — thực ra
              // là chữ vỡ dòng đè lên nhau): `.row` (ManagerApprovalQueue.module.css)
              // là CSS Grid 6 cột hẹp (40px/1fr/120px/130px/160px/100px) thiết kế
              // cho bảng đơn nghỉ phép — `flexDirection` không có tác dụng gì
              // trên `display: grid` nên 3 khối con của card này bị nhồi vào 3
              // cột hẹp đầu (tên NV rơi đúng cột 40px, vỡ chữ từng dòng, đè lên
              // khối mô tả ở cột 1fr bên cạnh). Ép `display: flex` để
              // flexDirection/alignItems phía dưới thật sự có tác dụng.
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              gap: "var(--nqh-space-2)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--nqh-space-3)", flexWrap: "wrap" }}>
              <span style={{ display: "flex", alignItems: "center", gap: "var(--nqh-space-2)" }}>
                <span className={`${styles.typeChip} ${styles.typeChipSwap}`}>
                  <SwapIcon size={13} /> Đổi ca
                </span>
                <span className={styles.requesterName}>
                  {item.requester_name} → {item.target_name}
                </span>
                {/* Dấu hiệu cảnh báo (2026-08-30 điểm 1): request tạo <24h trước
                    giờ bắt đầu ca — CHỈ cảnh báo, Quản Lý vẫn được duyệt.
                    A5 (2026-09-03): chip riêng nền đỏ đặc (warning24hChip) thay
                    vì mượn priorityHigh nền nhạt — màu cảnh báo phải rõ ràng. */}
                {item.warning_24h && (
                  <span className={styles.warning24hChip} title="Request tạo trong vòng 24h trước giờ bắt đầu ca — chỉ cảnh báo, vẫn duyệt được">
                    <WarningIcon /> Trong 24h
                  </span>
                )}
              </span>
              <span className={styles.requestedAt}>{formatDateTime(item.created_at)}</span>
            </div>
            <div className={styles.description}>
              Ca {formatDateTime(`${item.date}T${item.start_time}`)}–{item.end_time} — Lý do: {item.reason || "(không có)"}
            </div>
            {rejectingId === item.schedule_id ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--nqh-space-2)" }}>
                <textarea
                  className={styles.modalTextarea}
                  placeholder="Lý do từ chối (bắt buộc)…"
                  rows={2}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  autoFocus
                />
                <span style={{ display: "flex", gap: "var(--nqh-space-2)", justifyContent: "flex-end" }}>
                  <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setRejectingId(null)}>
                    Huỷ
                  </button>
                  <button
                    className={`${styles.btn} ${styles.btnDanger}`}
                    disabled={!rejectReason.trim() || approveMutation.isPending}
                    onClick={() => confirmReject(item)}
                  >
                    Xác nhận từ chối
                  </button>
                </span>
              </div>
            ) : (
              <span style={{ display: "flex", gap: "var(--nqh-space-2)", justifyContent: "flex-end" }}>
                <button
                  className={`${styles.btn} ${styles.btnDanger}`}
                  disabled={approveMutation.isPending}
                  onClick={() => startReject(item)}
                >
                  Từ chối
                </button>
                <button
                  className={`${styles.btn} ${styles.btnSuccess}`}
                  disabled={approveMutation.isPending}
                  onClick={() => handleApprove(item)}
                >
                  Duyệt
                </button>
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ShiftSwapReviewPage;
